import React, { useState } from 'react'
import { QRCodeSVG } from 'qrcode.react'
import { useNavigate } from 'react-router-dom'

import {
  formatShortDate,
  getTimezoneDate,
  sort,
  timezones,
} from '../../utilities/helpers'

import Stack from 'react-bootstrap/Stack'
import Modal from 'react-bootstrap/Modal'
import Button from 'react-bootstrap/Button'

import { ApplePassButton } from '../QRTicketCard/ApplePassButton'
import { GooglePassButton } from '../QRTicketCard/GooglePassButton'
import AccessPassTransferModal from '../AccessPassCard/AccessPassTransferModal'

function formatSeatPosition(accessPass) {
  if (accessPass?.generalAdmission) {
    return accessPass?.sectionNumber ? `GA ${accessPass.sectionNumber}` : 'GA'
  }

  const parts = []
  if (accessPass?.sectionNumber)
    parts.push(`Section ${accessPass.sectionNumber}`)
  if (accessPass?.rowNumber) parts.push(`Row ${accessPass.rowNumber}`)
  if (accessPass?.seatNumber) parts.push(`Seat ${accessPass.seatNumber}`)
  return parts.join(' · ') || null
}

function getSeasonLabel(eventPackage, events, timezone) {
  if (eventPackage?.season?.name) return eventPackage.season.name

  const firstEvent = sort(events || eventPackage?.events || [])[0]
  const eventStart = getTimezoneDate(firstEvent?.start, timezone)
  return eventStart ? eventStart.format('YYYY') : null
}

function getStatusLabel(status) {
  if (status === 'revoked') return 'Revoked'
  if (status === 'expired') return 'Expired'
  return 'Active'
}

export default function SeasonPassSummary({
  accessPass,
  eventPackage,
  holderName,
  onViewTickets,
  showViewTicketsButton = true,
}) {
  const [showQR, setShowQR] = useState(false)
  const [showTransfer, setShowTransfer] = useState(false)
  const [wasTransferred, setWasTransferred] = useState(false)
  const navigate = useNavigate()

  const events = sort(accessPass?.events || eventPackage?.events || [])
  const timezone = timezones(
    eventPackage?.venue?.timezone || events[0]?.venue?.timezone
  )
  const seasonLabel = getSeasonLabel(eventPackage, events, timezone)
  const seatLabel = formatSeatPosition(accessPass)
  const gamesCount = events.length
  const isActive = accessPass?.status === 'active'

  const walletEvent =
    sort(eventPackage?.events)?.[0] || sort(accessPass?.events)?.[0] || null

  const walletObj = {
    ...accessPass,
    uuid: accessPass?.uuid,
    checkInCode: accessPass?.checkInCode,
    sectionNumber: accessPass?.sectionNumber,
    rowNumber: accessPass?.rowNumber,
    seatNumber: accessPass?.seatNumber,
    generalAdmission: accessPass?.generalAdmission,
    name: accessPass?.name,
    accessPass: true,
  }

  return (
    <>
      <div className="package-pass-tickets__summary">
        <div className="package-pass-tickets__summary-header">
          <p className="package-pass-tickets__summary-header-title mb-0">
            {accessPass?.name || eventPackage?.name || 'Season pass'}
          </p>
          {accessPass?.checkInCode && (
            <span className="package-pass-tickets__summary-header-code">
              No. {accessPass.checkInCode}
            </span>
          )}
        </div>

        <div className="package-pass-tickets__summary-body">
          {accessPass?.checkInCode && (
            <button
              type="button"
              className="package-pass-tickets__summary-qr-btn"
              aria-label="Open QR code and wallet passes"
              onClick={() => setShowQR(true)}
            >
              <span className="package-pass-tickets__summary-qr-frame">
                <QRCodeSVG value={accessPass.checkInCode} size={72} />
                <span
                  className="package-pass-tickets__summary-qr-expand"
                  aria-hidden="true"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                  >
                    <path
                      d="M15 3H21V9M9 21H3V15M21 3L14 10M3 21L10 14"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </span>
              </span>
              <span className="package-pass-tickets__summary-qr-label">
                Tap for QR &amp; wallet
              </span>
            </button>
          )}
          <div className="flex-grow-1 min-w-0">
            {holderName && (
              <p className="package-pass-tickets__summary-holder">
                {holderName}
              </p>
            )}
            {seatLabel && (
              <p className="package-pass-tickets__summary-seat">{seatLabel}</p>
            )}
            {accessPass?.checkInCode && (
              <p className="package-pass-tickets__summary-hint">
                Tap the QR code to scan at entry or add to Apple/Google Wallet.
              </p>
            )}
          </div>
        </div>

        <div className="package-pass-tickets__summary-stats">
          <div>
            <p className="package-pass-tickets__summary-stat-label">
              Games included
            </p>
            <p className="package-pass-tickets__summary-stat-value">
              {gamesCount} home {gamesCount === 1 ? 'game' : 'games'}
            </p>
          </div>
          <div>
            <p className="package-pass-tickets__summary-stat-label">Season</p>
            <p className="package-pass-tickets__summary-stat-value">
              {seasonLabel || '—'}
            </p>
          </div>
          <div>
            <p className="package-pass-tickets__summary-stat-label">Status</p>
            <p
              className={`package-pass-tickets__summary-stat-value${
                isActive
                  ? ' package-pass-tickets__summary-stat-value--active'
                  : ''
              }`}
            >
              {getStatusLabel(accessPass?.status)}
            </p>
          </div>
        </div>

        {walletEvent && (
          <div className="package-pass-tickets__wallet">
            <p className="package-pass-tickets__wallet-label">
              Or add directly to wallet
            </p>
            <Stack
              direction="horizontal"
              gap={3}
              className="btn-group-flex flex-wrap mt-0 justify-content-center"
            >
              <ApplePassButton alwaysShow event={walletEvent} obj={walletObj} />
              <GooglePassButton
                alwaysShow
                event={walletEvent}
                obj={walletObj}
              />
            </Stack>
          </div>
        )}

        {isActive && (
          <div className="px-3">
            <Button
              variant="outline-light"
              className="mt-3 mb-3 w-100"
              onClick={() => setShowTransfer(true)}
            >
              Transfer season pass
            </Button>
          </div>
        )}

        {showViewTicketsButton && gamesCount > 0 && (
          <button
            type="button"
            className="package-pass-tickets__summary-footer-btn"
            onClick={onViewTickets}
          >
            View all {gamesCount} game tickets
            <span aria-hidden="true">→</span>
          </button>
        )}
      </div>

      <Modal show={showQR} onHide={() => setShowQR(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{accessPass?.name || 'Season pass'}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="access-pass-card__qr-modal-body text-center">
          {accessPass?.checkInCode && (
            <QRCodeSVG
              width="217"
              height="217"
              className="access-pass-card__qr-modal-code mb-3 p-0"
              value={accessPass.checkInCode}
            />
          )}
          <p className="small text-secondary mb-0">
            Show this code at entry for any included event.
          </p>
          {walletEvent && (
            <>
              <p className="small text-secondary mt-3 mb-0">
                Add this pass to your phone wallet below.
              </p>
              <Stack
                direction="horizontal"
                gap={3}
                className="access-pass-card__modal-wallet btn-group-flex flex-wrap mt-3 justify-content-center"
              >
                <ApplePassButton
                  alwaysShow
                  event={walletEvent}
                  obj={walletObj}
                />
                <GooglePassButton
                  alwaysShow
                  event={walletEvent}
                  obj={walletObj}
                />
              </Stack>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={() => setShowQR(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
      <AccessPassTransferModal
        accessPass={accessPass}
        show={showTransfer}
        onTransferred={() => setWasTransferred(true)}
        onHide={() => {
          setShowTransfer(false)
          if (wasTransferred) navigate('/my-transfers')
        }}
      />
    </>
  )
}
